import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  UserRound, LayoutGrid, Lock, KeyRound, Ban, SlidersHorizontal, CreditCard, AlertTriangle,
} from 'lucide-react';
import AdminShell from '../../components/AdminShell.jsx';
import AdminSectionNav from '../../components/AdminSectionNav.jsx';
import Alert from '../../components/Alert.jsx';
import { useFlash } from '../../context/ToastContext.jsx';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import GrantsMatrix from '../../components/GrantsMatrix.jsx';
import StartggTokenCard from '../../components/StartggTokenCard.jsx';
import Loader from '../../components/Loader.jsx';
import { api } from '../../api/client.js';
import { useAuth } from '../../context/AuthContext.jsx';

function generatePassword() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

// Ids must match the card ids below — AdminSectionNav observes them.
const SECTIONS = [
  { id: 'sec-identity', label: 'Identity' },
  { id: 'sec-grants', label: 'Grants' },
  { id: 'sec-password', label: 'Password' },
  { id: 'sec-key', label: 'Overlay key' },
  { id: 'sec-restrictions', label: 'Restrictions' },
  { id: 'sec-features', label: 'Features' },
  { id: 'sec-licensing', label: 'Licensing' },
  { id: 'sec-danger', label: 'Danger zone', danger: true },
];

// Signed run-credit adjustments (comps, refund restores, bundle prepays —
// licensing brief §8.3's human-in-the-loop flows). Every apply is one
// append-only ledger row with a mandatory reason, audited server-side.
function CreditAdjuster({ userId, packages, onFlash }) {
  const [packageId, setPackageId] = useState('');
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [applying, setApplying] = useState(false);
  // Balance reset (migration 0036): forces the balance to an exact number
  // for untreated issues — one race-safe adjustment row server-side.
  const [resetTo, setResetTo] = useState('');
  const [resetReason, setResetReason] = useState('');
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  const parsed = Number(delta);
  const valid = packageId && Number.isInteger(parsed) && parsed !== 0 && reason.trim();
  const resetParsed = Number(resetTo);
  const resetValid = packageId && Number.isInteger(resetParsed) && resetParsed >= 0 && resetReason.trim();
  const pkgName = (packages ?? []).find((p) => p.id === packageId)?.name ?? 'this package';

  async function apply() {
    setApplying(true);
    try {
      const res = await api.adminAdjustCredits(userId, {
        packageId,
        delta: parsed,
        reason: reason.trim(),
      });
      onFlash({
        kind: 'success',
        msg: `${parsed > 0 ? '+' : ''}${parsed} run${Math.abs(parsed) === 1 ? '' : 's'} applied — new balance ${res.balance}.`,
      });
      setDelta('');
      setReason('');
    } catch (err) {
      onFlash({ kind: 'danger', msg: err.message });
    } finally {
      setApplying(false);
    }
  }

  return (
    <div>
      <div className="adm-card-label mb-2">Run credits</div>
      <div className="row g-2 align-items-end">
        <div className="col-md-4">
          <label className="adm-flabel" htmlFor="credit-pkg">Package</label>
          <select
            id="credit-pkg"
            className="adm-uselect"
            value={packageId}
            onChange={(e) => setPackageId(e.target.value)}
          >
            <option value="">Choose…</option>
            {(packages ?? []).map((p) => (
              <option value={p.id} key={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="adm-flabel" htmlFor="credit-delta">± runs</label>
          <input
            id="credit-delta"
            type="number"
            className="adm-uinput"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder="+3"
          />
        </div>
        <div className="col-md-4">
          <label className="adm-flabel" htmlFor="credit-reason">Reason (required)</label>
          <input
            id="credit-reason"
            className="adm-uinput"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Trio bundle prepay"
          />
        </div>
        <div className="col-auto">
          <button
            type="button"
            className="btn-ghost btn-ghost--primary btn-sm"
            disabled={!valid || applying}
            onClick={apply}
          >
            {applying ? 'Applying…' : 'Apply'}
          </button>
        </div>
      </div>
      <div className="form-text">
        Positive adds runs (comps, bundle prepays, refund restores); negative
        removes them. Every adjustment lands in the credit ledger and the
        audit log with your name on it.
      </div>

      <div className="row g-2 align-items-end mt-2">
        <div className="col-md-3">
          <label className="adm-flabel" htmlFor="credit-reset-to">Set balance to</label>
          <input
            id="credit-reset-to"
            type="number"
            min="0"
            className="adm-uinput"
            value={resetTo}
            onChange={(e) => setResetTo(e.target.value)}
            placeholder="0"
          />
        </div>
        <div className="col-md-4">
          <label className="adm-flabel" htmlFor="credit-reset-reason">Reason (required)</label>
          <input
            id="credit-reset-reason"
            className="adm-uinput"
            value={resetReason}
            onChange={(e) => setResetReason(e.target.value)}
            placeholder="e.g. duplicate webhook credit"
          />
        </div>
        <div className="col-auto">
          <button
            type="button"
            className="btn-ghost btn-ghost--danger btn-sm"
            disabled={!resetValid || resetting}
            onClick={() => setResetConfirm(true)}
          >
            {resetting ? 'Resetting…' : 'Reset balance'}
          </button>
        </div>
      </div>
      <div className="form-text">
        Forces the balance to an exact number (uses the same package selector
        above) — the correcting delta is computed race-safely server-side and
        lands as one ledger adjustment. For untreated issues only; day-to-day
        changes belong in ± runs.
      </div>

      <ConfirmModal
        open={resetConfirm}
        title={`Reset ${pkgName} balance?`}
        body={(
          <p className="mb-0">
            This sets the run balance for <strong>{pkgName}</strong> to{' '}
            <strong>{resetTo || '0'}</strong>, whatever it is now. The correction
            is recorded in the ledger and audit log.
          </p>
        )}
        confirmLabel="Reset balance"
        confirmKind="danger"
        onConfirm={async () => {
          setResetConfirm(false);
          setResetting(true);
          try {
            const res = await api.adminResetCredits(userId, {
              packageId,
              setTo: resetParsed,
              reason: resetReason.trim(),
            });
            onFlash({
              kind: 'success',
              msg: `Balance reset to ${res.balance}${res.delta != null ? ` (correction ${res.delta > 0 ? '+' : ''}${res.delta})` : ''}.`,
            });
            setResetTo('');
            setResetReason('');
          } catch (err) {
            onFlash({ kind: 'danger', msg: err.message });
          } finally {
            setResetting(false);
          }
        }}
        onClose={() => setResetConfirm(false)}
      />
    </div>
  );
}

export default function UserEdit() {
  const { id } = useParams();
  const { user: me } = useAuth();

  const [packages, setPackages] = useState(null);
  const [user, setUser] = useState(null);
  const [grants, setGrants] = useState(new Set());

  // Identity form state — initialized after the user record loads.
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('user');
  const [identityDirty, setIdentityDirty] = useState(false);

  // Password reset state.
  const [newPassword, setNewPassword] = useState('');
  const [resetOpen, setResetOpen] = useState(false);

  // Rotate-key + delete confirms.
  const [rotateOpen, setRotateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [busy, setBusy] = useState(false);
  const setFlash = useFlash();
  const [loadError, setLoadError] = useState(null);

  function load() {
    setUser(null);
    setLoadError(null);
    // Pass the target user ID so the matrix shows shared overlays plus
    // this user's own private overlays — but not other users' privates.
    Promise.all([
      api.adminGetUser(id),
      api.adminListPackages(id),
    ]).then(([userResp, pkgResp]) => {
      setUser(userResp.user);
      setUsername(userResp.user.username);
      setRole(userResp.user.role);
      setGrants(new Set(userResp.overlayIds ?? []));
      setIdentityDirty(false);
      setPackages(pkgResp?.packages ?? []);
    }).catch((e) => setLoadError(e.message));
  }

  useEffect(() => { load(); }, [id]);

  async function saveIdentity() {
    setBusy(true);
    try {
      const patch = {};
      if (username !== user.username) patch.username = username.trim();
      if (role !== user.role) patch.role = role;
      if (Object.keys(patch).length === 0) return;
      await api.adminUpdateUser(id, patch);
      setFlash({ kind: 'success', msg: 'Identity updated.' });
      load();
    } catch (e) {
      setFlash({ kind: 'danger', msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  async function saveGrants() {
    setBusy(true);
    try {
      await api.adminSetUserOverlays(id, [...grants]);
      setFlash({ kind: 'success', msg: 'Grants updated.' });
    } catch (e) {
      setFlash({ kind: 'danger', msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    setBusy(true);
    try {
      await api.adminResetPassword(id, newPassword);
      setFlash({ kind: 'success', msg: 'Password reset. Hand the new one to the user.' });
      setNewPassword('');
      setResetOpen(false);
    } catch (e) {
      setFlash({ kind: 'danger', msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  async function rotateKey() {
    setBusy(true);
    try {
      const resp = await api.adminRotateUserKey(id);
      setUser((u) => ({ ...u, overlayKey: resp.overlayKey }));
      setFlash({
        kind: 'warning',
        msg: 'Overlay key rotated. The user must re-paste their OBS URLs.',
      });
      setRotateOpen(false);
    } catch (e) {
      setFlash({ kind: 'danger', msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  async function softDelete() {
    setBusy(true);
    try {
      await api.adminDeleteUser(id);
      setFlash({ kind: 'success', msg: `${user.username} disabled.` });
      load();
      setDeleteOpen(false);
    } catch (e) {
      setFlash({ kind: 'danger', msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    setBusy(true);
    try {
      await api.adminUpdateUser(id, { deletedAt: null });
      setFlash({ kind: 'success', msg: `${user.username} restored.` });
      load();
    } catch (e) {
      setFlash({ kind: 'danger', msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  if (!user) {
    // The rest of the body dereferences user.*, so loading keeps its early
    // return — but a failed load (bad /admin/users/<id>, API error) must
    // show the error instead of spinning forever.
    return (
      <AdminShell crumb="User">
        {loadError ? (
          <Alert kind="danger">{loadError}</Alert>
        ) : (
          <Loader label="Loading…" />
        )}
      </AdminShell>
    );
  }

  const isSelf = user.id === me?.id;

  return (
    <AdminShell crumb={user.username}>
      <div className="adm-edit-grid">
        <div>
        <header className="adm-head adm-rv" style={{ animationDelay: '80ms' }}>
          <h1 className="adm-head__title">
            <span className="section-marker" aria-hidden="true">◆</span> {user.username}
            {user.deletedAt && (
              <span className="status-tag status-tag--bad ms-2">deleted</span>
            )}
            {isSelf && <span className="adm-you-chip ms-2">you</span>}
          </h1>
          <p className="text-muted small mb-0 font-monospace">{user.id}</p>
        </header>


        {/* ── Identity ───────────────────────────────────── */}
        <section className="adm-esec adm-rvl" id="sec-identity" style={{ animationDelay: '140ms' }}>
          <div className="adm-group__head">
            <span className="adm-initial adm-initial--icon" aria-hidden="true"><UserRound size={17} /></span>
            <span className="adm-group__name">Identity</span>
            <span className="adm-group__summary">username · role</span>
          </div>
          <div className="adm-esec__body">
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <label className="adm-flabel" htmlFor="ue-username">Username</label>
                <input
                  id="ue-username"
                  type="text"
                  className="adm-uinput"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setIdentityDirty(true); }}
                  disabled={!!user.deletedAt}
                />
              </div>
              <div className="col-md-4">
                <label className="adm-flabel" htmlFor="ue-role">Role</label>
                <select
                  id="ue-role"
                  className="adm-uselect"
                  value={role}
                  onChange={(e) => { setRole(e.target.value); setIdentityDirty(true); }}
                  disabled={!!user.deletedAt || isSelf}
                  title={isSelf ? "Can't change your own role" : ''}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="col-md-2 text-end">
                <button
                  type="button"
                  className="btn-pill btn-pill--primary w-100 justify-content-center"
                  disabled={busy || !identityDirty || !!user.deletedAt}
                  onClick={saveIdentity}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ── Overlay grants ────────────────────────────── */}
        <section className="adm-esec adm-rvl" id="sec-grants" style={{ animationDelay: '190ms' }}>
          <div className="adm-group__head">
            <span className="adm-initial adm-initial--icon" aria-hidden="true"><LayoutGrid size={17} /></span>
            <span className="adm-group__name">Overlay grants</span>
            <span className="adm-group__summary">{grants.size} granted</span>
            <span className="adm-group__act">
              <button
                type="button"
                className="btn-pill btn-pill--primary"
                disabled={busy || !!user.deletedAt}
                onClick={saveGrants}
              >
                Save grants
              </button>
            </span>
          </div>
          <div className="adm-esec__body">
            {role === 'admin' && (
              <p className="text-muted small mb-3">
                Admins implicitly have access to every overlay regardless of these grants.
              </p>
            )}
            <GrantsMatrix
              packages={packages}
              value={grants}
              onChange={setGrants}
              disabled={!!user.deletedAt}
            />
          </div>
        </section>

        {/* ── Password ──────────────────────────────────── */}
        <section className="adm-esec adm-rvl" id="sec-password" style={{ animationDelay: '240ms' }}>
          <div className="adm-group__head">
            <span className="adm-initial adm-initial--icon" aria-hidden="true"><Lock size={17} /></span>
            <span className="adm-group__name">Password</span>
            <span className="adm-group__summary">plaintext handoff</span>
          </div>
          <div className="adm-esec__body">
            <div className="row g-3 align-items-end">
              <div className="col-md-9">
                <label className="adm-flabel" htmlFor="ue-newpass">New password</label>
                <div className="d-flex gap-2 align-items-end">
                  <input
                    id="ue-newpass"
                    type="text"
                    className="adm-uinput adm-uinput--mono flex-grow-1"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={8}
                    placeholder="8+ characters"
                    disabled={!!user.deletedAt}
                  />
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setNewPassword(generatePassword())}
                    disabled={!!user.deletedAt}
                  >
                    Generate
                  </button>
                </div>
              </div>
              <div className="col-md-3 text-end">
                <button
                  type="button"
                  className="btn-ghost btn-ghost--danger w-100 justify-content-center"
                  disabled={busy || newPassword.length < 8 || !!user.deletedAt}
                  onClick={() => setResetOpen(true)}
                >
                  Reset
                </button>
              </div>
            </div>
            <div className="form-text">
              Shown in plaintext so you can copy and hand it to the user.
            </div>
          </div>
        </section>

        {/* ── Overlay key ───────────────────────────────── */}
        <section className="adm-esec adm-rvl" id="sec-key" style={{ animationDelay: '290ms' }}>
          <div className="adm-group__head">
            <span className="adm-initial adm-initial--icon" aria-hidden="true"><KeyRound size={17} /></span>
            <span className="adm-group__name">Overlay access key</span>
            <span className="adm-group__summary">the ?key= in OBS URLs</span>
          </div>
          <div className="adm-esec__body">
            <div className="d-flex gap-2 align-items-center">
              <code className="flex-grow-1 p-2 token-display">
                {user.overlayKey}
              </code>
              <button
                type="button"
                className="btn-ghost btn-ghost--warning btn-sm"
                disabled={busy || !!user.deletedAt}
                onClick={() => setRotateOpen(true)}
              >
                Rotate
              </button>
            </div>
            <div className="form-text">
              The <code>?key=</code> in the user's OBS Browser Source URLs. Rotating
              invalidates pasted URLs — they'll need to copy fresh ones from their
              production page.
            </div>
          </div>
        </section>

        {/* ── Restrictions ──────────────────────────────── */}
        <section className="adm-esec adm-rvl" id="sec-restrictions" style={{ animationDelay: '340ms' }}>
          <div className="adm-group__head">
            <span className="adm-initial adm-initial--icon" aria-hidden="true"><Ban size={17} /></span>
            <span className="adm-group__name">Restrictions</span>
            <span className="adm-group__summary">what this account can't do</span>
          </div>
          <div className="adm-esec__body">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="design-requests-disabled"
                checked={!!user.designRequestsDisabled}
                disabled={busy || !!user.deletedAt}
                onChange={async (e) => {
                  setBusy(true);
                  try {
                    await api.adminUpdateUser(id, {
                      designRequestsDisabled: e.target.checked,
                    });
                    setFlash({
                      kind: 'success',
                      msg: e.target.checked
                        ? 'Design requests disabled for this user.'
                        : 'Design requests re-enabled.',
                    });
                    load();
                  } catch (err) {
                    setFlash({ kind: 'danger', msg: err.message });
                  } finally {
                    setBusy(false);
                  }
                }}
              />
              <label className="form-check-label" htmlFor="design-requests-disabled">
                Disable design requests
              </label>
            </div>
            <div className="form-text">
              When on, this user can't submit new "Request a Custom Overlay"
              forms from <code>/access/&lt;package&gt;</code>. Existing pending
              requests are kept; only new submissions are blocked.
            </div>
          </div>
        </section>

        {/* ── Features ──────────────────────────────── */}
        <section className="adm-esec adm-rvl" id="sec-features" style={{ animationDelay: '390ms' }}>
          <div className="adm-group__head">
            <span className="adm-initial adm-initial--icon" aria-hidden="true"><SlidersHorizontal size={17} /></span>
            <span className="adm-group__name">Features</span>
            <span className="adm-group__summary">console surfaces</span>
          </div>
          <div className="adm-esec__body">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="sponsors-enabled"
                checked={!!user.sponsorsEnabled}
                disabled={busy || !!user.deletedAt}
                onChange={async (e) => {
                  setBusy(true);
                  try {
                    await api.adminUpdateUser(id, {
                      sponsorsEnabled: e.target.checked,
                    });
                    setFlash({
                      kind: 'success',
                      msg: e.target.checked
                        ? 'Sponsors section enabled for this user.'
                        : 'Sponsors section disabled for this user.',
                    });
                    load();
                  } catch (err) {
                    setFlash({ kind: 'danger', msg: err.message });
                  } finally {
                    setBusy(false);
                  }
                }}
              />
              <label className="form-check-label" htmlFor="sponsors-enabled">
                Enable sponsors section
              </label>
            </div>
            <div className="form-text">
              When on, this user sees the Diamond / Gold / Silver sponsor
              logo uploaders on their <code>/:slug</code> production page.
              Default off for new users.
            </div>

            <hr className="my-3" />

            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="obs-export-enabled"
                checked={user.obsExportEnabled !== false}
                disabled={busy || !!user.deletedAt}
                onChange={async (e) => {
                  setBusy(true);
                  try {
                    await api.adminUpdateUser(id, {
                      obsExportEnabled: e.target.checked,
                    });
                    setFlash({
                      kind: 'success',
                      msg: e.target.checked
                        ? 'OBS export enabled for this user.'
                        : 'OBS export disabled for this user.',
                    });
                    load();
                  } catch (err) {
                    setFlash({ kind: 'danger', msg: err.message });
                  } finally {
                    setBusy(false);
                  }
                }}
              />
              <label className="form-check-label" htmlFor="obs-export-enabled">
                Enable OBS export
              </label>
            </div>
            <div className="form-text">
              When on, this user sees the "Export to OBS" button on their{' '}
              <code>/:slug</code> production page. Default on.
            </div>

            <hr className="my-3" />

            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="vmix-export-enabled"
                checked={user.vmixExportEnabled !== false}
                disabled={busy || !!user.deletedAt}
                onChange={async (e) => {
                  setBusy(true);
                  try {
                    await api.adminUpdateUser(id, {
                      vmixExportEnabled: e.target.checked,
                    });
                    setFlash({
                      kind: 'success',
                      msg: e.target.checked
                        ? 'vMix export enabled for this user.'
                        : 'vMix export disabled for this user.',
                    });
                    load();
                  } catch (err) {
                    setFlash({ kind: 'danger', msg: err.message });
                  } finally {
                    setBusy(false);
                  }
                }}
              />
              <label className="form-check-label" htmlFor="vmix-export-enabled">
                Enable vMix export
              </label>
            </div>
            <div className="form-text">
              When on, this user sees the "Export to vMix" button on their{' '}
              <code>/:slug</code> production page. Default on.
            </div>
          </div>
        </section>

        {/* ── Licensing ──────────────────────────────── */}
        <section className="adm-esec adm-rvl" id="sec-licensing" style={{ animationDelay: '440ms' }}>
          <div className="adm-group__head">
            <span className="adm-initial adm-initial--icon" aria-hidden="true"><CreditCard size={17} /></span>
            <span className="adm-group__name">Licensing</span>
            <span className="adm-group__summary">billing + run credits</span>
          </div>
          <div className="adm-esec__body">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="billing-enforced"
                checked={!!user.billingEnforced}
                disabled={busy || !!user.deletedAt}
                onChange={async (e) => {
                  setBusy(true);
                  try {
                    await api.adminUpdateUser(id, {
                      billingEnforced: e.target.checked,
                    });
                    setFlash({
                      kind: 'success',
                      msg: e.target.checked
                        ? 'Billing enforced — overlays now run under activation routing (tester mode until a run is activated).'
                        : 'Billing enforcement off — overlays behave exactly as pre-licensing.',
                    });
                    load();
                  } catch (err) {
                    setFlash({ kind: 'danger', msg: err.message });
                  } finally {
                    setBusy(false);
                  }
                }}
              />
              <label className="form-check-label" htmlFor="billing-enforced">
                Enforce billing (activation routing + tester mode)
              </label>
            </div>
            <div className="form-text mb-3">
              The per-client kill switch: turning this off instantly returns the
              user to pre-billing behavior — no redeploy, applies within one
              request. Never flip a client on during their event weekend.
            </div>

            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="tournament-history-enabled"
                checked={user.tournamentHistoryEnabled !== false}
                disabled={busy || !!user.deletedAt}
                onChange={async (e) => {
                  setBusy(true);
                  try {
                    await api.adminUpdateUser(id, {
                      tournamentHistoryEnabled: e.target.checked,
                    });
                    setFlash({
                      kind: 'success',
                      msg: e.target.checked
                        ? 'Tournament history shown for this user.'
                        : 'Tournament history hidden for this user.',
                    });
                    load();
                  } catch (err) {
                    setFlash({ kind: 'danger', msg: err.message });
                  } finally {
                    setBusy(false);
                  }
                }}
              />
              <label className="form-check-label" htmlFor="tournament-history-enabled">
                Show tournament history
              </label>
            </div>
            <div className="form-text mb-3">
              When on, completed runs appear on the console — the license-history
              fold on the Tournament card, or the standalone "Tournament history"
              card for unenforced accounts. Hiding it never deletes anything.
              Default on.
            </div>

            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="portrait-masks-enabled"
                checked={user.portraitMasksEnabled === true}
                disabled={busy || !!user.deletedAt}
                onChange={async (e) => {
                  setBusy(true);
                  try {
                    await api.adminUpdateUser(id, {
                      portraitMasksEnabled: e.target.checked,
                    });
                    setFlash({
                      kind: 'success',
                      msg: e.target.checked
                        ? 'Tekken 8 character picker shown for this user.'
                        : 'Tekken 8 character picker hidden for this user.',
                    });
                    load();
                  } catch (err) {
                    setFlash({ kind: 'danger', msg: err.message });
                  } finally {
                    setBusy(false);
                  }
                }}
              />
              <label className="form-check-label" htmlFor="portrait-masks-enabled">
                Tekken 8 In-Match labels behind the portraits
              </label>
            </div>
            <div className="form-text mb-3">
              Shows a character picker on the console's Overlays tab. Once a
              side's character is picked for the match on stream, that In-Match
              label runs on under the game's portrait. Needs an In-Match grant
              re-applied since this feature (Use default). Default off.
            </div>

            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="start-match-enabled"
                checked={user.startMatchEnabled === true}
                disabled={busy || !!user.deletedAt}
                onChange={async (e) => {
                  setBusy(true);
                  try {
                    await api.adminUpdateUser(id, {
                      startMatchEnabled: e.target.checked,
                    });
                    setFlash({
                      kind: 'success',
                      msg: e.target.checked
                        ? 'Start match shown for this user.'
                        : 'Start match hidden for this user; auto-start stops too.',
                    });
                    load();
                  } catch (err) {
                    setFlash({ kind: 'danger', msg: err.message });
                  } finally {
                    setBusy(false);
                  }
                }}
              />
              <label className="form-check-label" htmlFor="start-match-enabled">
                Run matches from the console (Tekken 8)
              </label>
            </div>
            <div className="form-text mb-3">
              The In-Match card gets Call and Start match for the match on the
              overlay, a switch that starts the next one when a match ends, and
              the score: games won, the winner, a DQ and a reset, all reported
              to start.gg. It acts with the start.gg token added below. Default off.
            </div>

            <StartggTokenCard userId={id} enabled={user.startMatchEnabled === true} onFlash={setFlash} />

            <CreditAdjuster userId={id} packages={packages} onFlash={setFlash} />
          </div>
        </section>

        {/* ── Danger zone ──────────────────────────────── */}
        <section className="adm-esec adm-esec--danger adm-rvl" id="sec-danger" style={{ animationDelay: '490ms' }}>
          <div className="adm-group__head">
            <span className="adm-initial adm-initial--icon" aria-hidden="true"><AlertTriangle size={17} /></span>
            <span className="adm-group__name">Danger zone</span>
            <span className="adm-group__summary">disable / restore</span>
          </div>
          <div className="adm-esec__body d-flex justify-content-between align-items-center">
            {user.deletedAt ? (
              <>
                <div>
                  <div>Account is currently <strong>disabled</strong>.</div>
                  <div className="text-muted small">
                    Disabled at {new Date(user.deletedAt).toLocaleString()}.
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-ghost btn-ghost--primary"
                  disabled={busy}
                  onClick={restore}
                >
                  Restore
                </button>
              </>
            ) : (
              <>
                <div>
                  <div>Disable this user account.</div>
                  <div className="text-muted small">
                    They can't log in, OBS URLs stop working. Reversible.
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-ghost btn-ghost--danger"
                  disabled={busy || isSelf}
                  title={isSelf ? "Can't disable yourself" : ''}
                  onClick={() => setDeleteOpen(true)}
                >
                  Disable
                </button>
              </>
            )}
          </div>
        </section>

        <ConfirmModal
          open={resetOpen}
          title="Reset password?"
          body={`Set a new password for ${user.username}. Their existing sessions stay active until they expire (max 12h).`}
          confirmLabel="Reset"
          confirmKind="danger"
          onConfirm={resetPassword}
          onClose={() => setResetOpen(false)}
        />
        <ConfirmModal
          open={rotateOpen}
          title="Rotate overlay key?"
          body={
            <>
              <p className="mb-2">A new <code>overlay_key</code> will be generated.</p>
              <p className="text-warning small mb-0">
                The user's currently-pasted OBS Browser Source URLs will stop returning
                data immediately. They need to copy fresh URLs from their production page.
              </p>
            </>
          }
          confirmLabel="Rotate"
          confirmKind="danger"
          onConfirm={rotateKey}
          onClose={() => setRotateOpen(false)}
        />
        <ConfirmModal
          open={deleteOpen}
          title={`Disable ${user.username}?`}
          body={
            <>
              <p className="mb-2">This will:</p>
              <ul className="text-muted mb-0">
                <li>Block any sign-in attempts</li>
                <li>Invalidate their OBS URLs</li>
                <li>Hide them from the default users list</li>
              </ul>
              <p className="text-muted small mb-0 mt-3">You can restore them later.</p>
            </>
          }
          confirmLabel="Disable"
          confirmKind="danger"
          onConfirm={softDelete}
          onClose={() => setDeleteOpen(false)}
        />
        </div>

        <AdminSectionNav sections={SECTIONS} />
      </div>
    </AdminShell>
  );
}
